import React from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import {
  Star,
  LayoutDashboard,
  BarChart3,
  MessageSquare,
  User,
  TrendingUp,
} from 'lucide-react';
import { usePageTitle } from '../lib/usePageTitle';

const buildCards = [
  {
    title: 'EVP & story',
    description:
      "We work with your team to define what's genuinely worth choosing about working there — distilled into a pitch that survives a skeptical candidate's second question.",
    icon: Star,
  },
  {
    title: 'Career page & content',
    description:
      'A career page, role stories, and founder/team content that gives a candidate a real feel for the company before they ever apply — not a generic careers template.',
    icon: LayoutDashboard,
  },
  {
    title: 'Candidate-facing presence',
    description:
      'Consistent presence across the places candidates actually check — LinkedIn, job posts, review sites — so the story holds up wherever they look you up.',
    icon: BarChart3,
  },
  {
    title: 'Recruiter-delivered',
    description:
      'Because our recruiters are already talking to your candidates directly, the story travels live in every conversation — not left to a webpage no one reads closely.',
    icon: MessageSquare,
  },
  {
    title: 'Team & culture content',
    description:
      'Short, honest content featuring the actual people a candidate would work with — the thing that does more to build trust than any polished brand video.',
    icon: User,
  },
  {
    title: 'Built to compound',
    description:
      'Every piece — EVP, page, content — is built once and reused across every role you hire for, so the brand gets stronger with each search rather than starting over.',
    icon: TrendingUp,
  },
];

const steps = [
  {
    number: '01',
    title: 'Brand & culture discovery',
    description:
      "A working session on what's genuinely distinctive about working at your company — not aspirational language, what's actually true.",
  },
  {
    number: '02',
    title: 'EVP & messaging',
    description:
      'We distill that into a clear, honest pitch — the version a skeptical candidate would actually believe.',
  },
  {
    number: '03',
    title: 'Career page & content build',
    description:
      'Career page copy and structure, role stories, and founder/team content, built around the EVP.',
  },
  {
    number: '04',
    title: 'Woven into every conversation',
    description:
      "Our recruiters carry the story into every candidate interaction, so it's reinforced live, not just published once and left.",
  },
];

const teamColors = ['#E4502F', '#D6A039', '#2670AD', '#1CAA3C', '#8B4FA0'];

const pillars = [
  { icon: '🚀', text: <>Ownership<br />from day one</> },
  { icon: '📈', text: <>Equity that<br />actually matters</> },
  { icon: '🌍', text: <>Remote-first,<br />always</> },
];

const roles = [
  { title: 'Senior Product Engineer', tag: 'Remote' },
  { title: 'Founding Growth Lead', tag: 'Hybrid' },
  { title: 'Product Designer', tag: 'Remote' },
];

const evpRows = [
  {
    icon: '🎯',
    title: 'Mission that matters',
    text: 'Solving a problem our team feels personally',
  },
  {
    icon: '⚡',
    title: 'Speed & ownership',
    text: 'Ship real work in week one, not month three',
  },
  {
    icon: '🤝',
    title: 'A team that ships',
    text: 'Small, senior, no layers between you and impact',
  },
];

const PersonIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="white">
    <circle cx="12" cy="9" r="4" />
    <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
  </svg>
);

export const EmployerBrandingPage: React.FC = () => {
  usePageTitle('Employer Branding | QuantumHire');

  return (
    <>
      <style>{`
        /* =========================================================
           EMPLOYER BRANDING PAGE
           Matches the QuantumHire reference site 1:1 (#view-employer-branding)
           ========================================================= */

        #view-employer-branding {
          --eb-paper: var(--paper, #ffffff);
          --eb-paper-dim: var(--paper-dim, #EBECE5);
          --eb-quantum: var(--quantum, #1CAA3C);
          --eb-quantum-deep: var(--quantum-deep, #0B5C22);
          --eb-quantum-tint: var(--quantum-tint, #E7F5EA);
          --eb-ink: var(--ink, #14150F);
          --eb-ink-soft: var(--ink-soft, #3A3C35);
          --eb-steel: var(--steel, #6B6F68);
          --eb-line: var(--line, rgba(20, 21, 15, 0.10));

          min-height: 100vh;
          background: var(--eb-paper-dim);
          color: var(--eb-ink);
        }

        /* =========================================================
           HERO
           ========================================================= */

        #view-employer-branding .prog-hero {
          padding: 88px 32px 64px;
          background:
            linear-gradient(
              135deg,
              var(--eb-paper-dim) 0%,
              var(--eb-quantum-tint) 55%,
              var(--eb-paper-dim) 100%
            );
          border-bottom: none;
        }

        #view-employer-branding .prog-hero-inner {
          max-width: 1180px;
          margin: 0 auto;
          text-align: left;

          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 48px;
          align-items: center;
        }

        #view-employer-branding .prog-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 10px;

          border: 1px solid rgba(11, 92, 34, 0.25);
          background: var(--eb-paper);

          padding: 7px 12px;
          border-radius: 2px;

          margin-bottom: 24px;

          font-family: 'JetBrains Mono', monospace;
          font-size: 11.5px;
          text-transform: uppercase;

          color: var(--eb-quantum-deep);
        }

        #view-employer-branding .dot-live {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: var(--eb-quantum);
          flex-shrink: 0;
        }

        #view-employer-branding .prog-hero h1 {
          max-width: 760px;

          margin: 0 0 18px;

          font-family: 'Poppins', sans-serif;
          font-size: clamp(32px, 4.6vw, 52px);
          line-height: 1.1;
          font-weight: 600;
          letter-spacing: -0.02em;

          color: var(--eb-ink);
        }

        #view-employer-branding .prog-hero h1 em {
          font-style: normal;
          color: var(--eb-quantum-deep);
        }

        #view-employer-branding .lede {
          max-width: 620px;

          margin: 0 auto 30px 0;

          font-family: 'Inter', sans-serif;
          font-size: 17px;
          line-height: 1.6;

          color: var(--eb-ink-soft);
        }

        #view-employer-branding .prog-hero-cta {
          display: flex;
          align-items: center;
          gap: 22px;
          justify-content: flex-start;
          flex-wrap: wrap;
        }

        #view-employer-branding .prog-btn-primary {
          display: inline-flex;
          align-items: center;
          justify-content: center;

          padding: 15px 26px;

          border-radius: 2px;

          background: var(--eb-quantum);
          color: var(--eb-paper);

          font-size: 15px;
          font-weight: 600;

          text-decoration: none;

          transition:
            transform 0.15s ease,
            background 0.15s ease;
        }

        #view-employer-branding .prog-btn-primary:hover {
          background: var(--eb-quantum-deep);
          transform: translateY(-1px);
        }

        /* =========================================================
           HERO MOCKUP
           ========================================================= */

        #view-employer-branding .eb-mockup-stage {
          position: relative;
          height: 460px;
        }

        #view-employer-branding .eb-browser {
          position: absolute;
          top: 0;
          left: 0;
          width: 84%;

          border-radius: 10px;
          overflow: hidden;

          background: var(--eb-paper);
          box-shadow: 0 24px 48px rgba(20, 21, 15, 0.16);
          transform: rotate(-1.5deg);
        }

        #view-employer-branding .eb-browser-chrome {
          display: flex;
          align-items: center;
          gap: 6px;

          padding: 9px 12px;

          background: var(--eb-paper-dim);
          border-bottom: 1px solid var(--eb-line);
        }

        #view-employer-branding .eb-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          display: inline-block;
        }

        #view-employer-branding .eb-url {
          margin-left: 10px;

          font-family: 'JetBrains Mono', monospace;
          font-size: 9.5px;
          color: var(--eb-steel);

          background: var(--eb-paper);
          padding: 3px 10px;
          border-radius: 10px;
          flex: 1;
        }

        #view-employer-branding .eb-browser-body {
          padding: 18px 20px;
        }

        #view-employer-branding .eb-page-nav {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 16px;
        }

        #view-employer-branding .eb-page-logo {
          width: 22px;
          height: 22px;
          border-radius: 5px;
          background: var(--eb-quantum);
        }

        #view-employer-branding .eb-page-navlinks {
          display: flex;
          gap: 14px;
        }

        #view-employer-branding .eb-page-navlinks span {
          font-family: 'Inter', sans-serif;
          font-size: 10px;
          color: var(--eb-steel);
          font-weight: 500;
        }

        #view-employer-branding .eb-page-hero {
          margin-bottom: 16px;
          padding-bottom: 16px;
          border-bottom: 1px solid var(--eb-line);
        }

        #view-employer-branding .eb-page-h1-text {
          font-family: 'Poppins', sans-serif;
          font-weight: 700;
          font-size: 17px;
          line-height: 1.25;
          color: var(--eb-ink);
          margin-bottom: 6px;
        }

        #view-employer-branding .eb-page-sub-text {
          font-size: 10.5px;
          color: var(--eb-steel);
          line-height: 1.5;
          margin-bottom: 10px;
        }

        #view-employer-branding .eb-page-btn {
          display: inline-block;
          font-family: 'JetBrains Mono', monospace;
          font-size: 9.5px;
          font-weight: 600;
          color: var(--eb-paper);
          background: var(--eb-quantum);
          padding: 6px 12px;
          border-radius: 5px;
        }

        #view-employer-branding .eb-pillars {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
          margin-bottom: 16px;
        }

        #view-employer-branding .eb-pillar {
          text-align: center;
        }

        #view-employer-branding .eb-pillar-icon {
          font-size: 16px;
          margin-bottom: 4px;
        }

        #view-employer-branding .eb-pillar-text {
          font-size: 8.5px;
          color: var(--eb-ink-soft);
          line-height: 1.35;
          font-weight: 500;
        }

        #view-employer-branding .eb-team-strip {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 14px;
        }

        #view-employer-branding .eb-avatar {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: 2px solid var(--eb-paper);
          margin-left: -6px;
          overflow: hidden;
        }

        #view-employer-branding .eb-avatar svg {
          width: 13px;
          height: 13px;
        }

        #view-employer-branding .eb-avatar:first-child {
          margin-left: 0;
        }

        #view-employer-branding .eb-team-label {
          font-size: 9px;
          color: var(--eb-quantum-deep);
          font-weight: 600;
          margin-left: 6px;
        }

        #view-employer-branding .eb-roles {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        #view-employer-branding .eb-role {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 7px 10px;
          border: 1px solid var(--eb-line);
          border-radius: 6px;
        }

        #view-employer-branding .eb-role span:first-child {
          font-size: 10px;
          color: var(--eb-ink);
          font-weight: 500;
        }

        #view-employer-branding .eb-role-tag {
          font-family: 'JetBrains Mono', monospace;
          font-size: 8px;
          color: var(--eb-quantum-deep);
          background: var(--eb-quantum-tint);
          padding: 2px 7px;
          border-radius: 8px;
        }

        /* EVP document */

        #view-employer-branding .eb-doc {
          position: absolute;
          bottom: 0;
          right: 0;
          width: 54%;

          background: var(--eb-paper);
          border-radius: 10px;

          padding: 20px 20px 22px;

          box-shadow: 0 20px 40px rgba(20, 21, 15, 0.18);
          transform: rotate(2deg);

          border: 1px solid var(--eb-line);
        }

        #view-employer-branding .eb-doc-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 12px;
        }

        #view-employer-branding .eb-doc-logo {
          width: 18px;
          height: 18px;
          border-radius: 5px;
          background: var(--eb-quantum);
        }

        #view-employer-branding .eb-doc-tag {
          font-family: 'JetBrains Mono', monospace;
          font-size: 8px;
          letter-spacing: 0.04em;
          color: var(--eb-quantum-deep);
          background: var(--eb-quantum-tint);
          padding: 3px 7px;
          border-radius: 10px;
        }

        #view-employer-branding .eb-doc-title-text {
          font-family: 'Poppins', sans-serif;
          font-weight: 700;
          font-size: 16px;
          color: var(--eb-ink);
          margin-bottom: 8px;
        }

        #view-employer-branding .eb-doc-row {
          display: flex;
          align-items: flex-start;
          gap: 9px;
          margin-top: 11px;
        }

        #view-employer-branding .eb-doc-icon {
          font-size: 14px;
          flex-shrink: 0;
        }

        #view-employer-branding .eb-doc-text {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        #view-employer-branding .eb-doc-text strong {
          font-size: 10.5px;
          color: var(--eb-ink);
          font-weight: 600;
        }

        #view-employer-branding .eb-doc-text span {
          font-size: 9px;
          color: var(--eb-steel);
          line-height: 1.4;
        }

        /* =========================================================
           GENERAL SECTIONS
           ========================================================= */

        #view-employer-branding .prog-section {
          padding: 80px 32px;
          background: var(--eb-paper);
        }

        #view-employer-branding .prog-section.tint {
          background: var(--eb-paper-dim);
        }

        #view-employer-branding .prog-section-head {
          max-width: 640px;
          margin: 0 auto 48px;
          text-align: center;
        }

        #view-employer-branding .prog-section-head h2 {
          margin: 0 0 12px;
          font-family: 'Poppins', sans-serif;
          font-size: clamp(24px, 3.2vw, 36px);
          line-height: 1.2;
          font-weight: 600;
          letter-spacing: -0.02em;
          color: var(--eb-ink);
        }

        #view-employer-branding .prog-section-head p {
          margin: 0;
          font-family: 'Inter', sans-serif;
          font-size: 15.5px;
          line-height: 1.6;
          color: var(--eb-steel);
        }

        /* =========================================================
           WHAT WE BUILD
           ========================================================= */

        #view-employer-branding .prog-grid-3 {
          max-width: 1180px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
        }

        #view-employer-branding .eb-card {
          background: var(--eb-paper);
          border: 2px solid rgba(28, 170, 60, 0.25);
          border-radius: 8px;
          padding: 28px 24px;
          transition: transform 0.18s, box-shadow 0.18s;
        }

        #view-employer-branding .eb-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 14px 28px rgba(20, 21, 15, 0.08);
        }

        #view-employer-branding .eb-card-icon {
          width: 48px;
          height: 48px;
          border-radius: 11px;
          background: var(--eb-quantum-tint);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 14px;
        }

        #view-employer-branding .eb-card-icon svg {
          width: 24px;
          height: 24px;
          stroke: var(--eb-quantum-deep);
          stroke-width: 2.2;
          fill: none;
        }

        #view-employer-branding .eb-card h3 {
          font-family: 'Poppins', sans-serif;
          font-size: 16.5px;
          line-height: 1.3;
          margin: 0 0 8px;
          font-weight: 700;
          color: var(--eb-ink);
        }

        #view-employer-branding .eb-card p {
          font-family: 'Inter', sans-serif;
          font-size: 13.5px;
          line-height: 1.6;
          margin: 0;
          color: var(--eb-steel);
        }

        /* =========================================================
           HOW IT WORKS
           ========================================================= */

        #view-employer-branding .prog-steps {
          max-width: 900px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          gap: 0;
        }

        #view-employer-branding .prog-step {
          display: flex;
          gap: 22px;
          padding: 26px 0;
          border-bottom: 1px solid var(--eb-line);
        }

        #view-employer-branding .prog-step:last-child {
          border-bottom: none;
        }

        #view-employer-branding .prog-step-num {
          min-width: 44px;
          flex-shrink: 0;

          background: var(--eb-quantum);
          color: var(--eb-paper);

          font-family: 'Poppins', sans-serif;
          font-size: 22px;
          font-weight: 700;
          line-height: 1;
        }

        #view-employer-branding .prog-step h3 {
          margin: 0 0 6px;
          font-family: 'Poppins', sans-serif;
          font-size: 16.5px;
          line-height: 1.3;
          font-weight: 600;
          letter-spacing: -0.02em;
          color: var(--eb-ink);
        }

        #view-employer-branding .prog-step p {
          margin: 0;
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          line-height: 1.6;
          color: var(--eb-steel);
        }

        /* =========================================================
           CTA
           NOTE: no max-width on the h2 here — the real site lets
           this heading run the full width of the strip so it stays
           on one line at normal desktop widths. Adding a max-width
           (like 760px) is what forces it to wrap to two lines.
           ========================================================= */

        #view-employer-branding .prog-cta-strip {
          padding: 70px 32px;
          text-align: center;
          background: linear-gradient(120deg, var(--eb-ink), var(--eb-quantum-deep));
        }

        #view-employer-branding .prog-cta-strip h2 {
          margin: 0 auto 14px;
          font-family: 'Poppins', sans-serif;
          font-size: clamp(24px, 3vw, 34px);
          line-height: 1.2;
          font-weight: 600;
          letter-spacing: -0.02em;
          color: var(--eb-paper);
          white-space: nowrap;
        }

        #view-employer-branding .prog-cta-strip p {
          max-width: 560px;
          margin: 0 auto 30px;
          font-family: 'Inter', sans-serif;
          font-size: 15.5px;
          line-height: 1.6;
          color: rgba(255, 255, 255, 0.7);
        }

        #view-employer-branding .prog-cta-strip .prog-hero-cta {
          justify-content: center;
        }

        #view-employer-branding .prog-cta-strip .prog-btn-primary {
          background: var(--eb-paper);
          color: var(--eb-quantum-deep);
        }

        #view-employer-branding .prog-cta-strip .prog-btn-primary:hover {
          background: #ffffff;
        }

        /* =========================================================
           RESPONSIVE
           ========================================================= */

        @media (max-width: 960px) {
          #view-employer-branding .prog-hero-inner {
            grid-template-columns: 1fr;
            text-align: center;
          }

          #view-employer-branding .prog-hero .prog-hero-cta {
            justify-content: center;
          }

          #view-employer-branding .lede {
            margin-left: auto;
            margin-right: auto;
          }

          #view-employer-branding .eb-mockup-stage {
            height: 400px;
            max-width: 440px;
            width: 100%;
            margin: 0 auto;
          }
        }

        @media (max-width: 900px) {
          #view-employer-branding .prog-grid-3 {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 640px) {
          #view-employer-branding .prog-section {
            padding: 64px 18px;
          }

          /* Below this width the heading may need to wrap again to
             avoid overflowing a narrow phone screen — that's fine,
             it's the same trade-off the real site makes. */
          #view-employer-branding .prog-cta-strip h2 {
            white-space: normal;
          }
        }

        @media (max-width: 480px) {
          #view-employer-branding .eb-mockup-stage {
            height: 360px;
          }

          #view-employer-branding .eb-page-h1-text {
            font-size: 15px;
          }
        }
      `}</style>

      <main id="view-employer-branding">

        {/* =====================================================
            HERO
            ===================================================== */}

        <header className="prog-hero">
          <div className="prog-hero-inner">

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="prog-eyebrow">
                <span className="dot-live" />
                QuantumHire Employer Branding
              </div>

              <h1>
                When your logo doesn't open doors yet,{' '}
                <em>your story</em> has to.
              </h1>

              <p className="lede">
                Candidates default to companies they already recognize. If
                you're a startup or a company without that name recognition
                yet, a clear employer brand is what closes the gap — and it's a
                distinct service from placement, built for founders and talent
                teams who need candidates to choose them on the merits, not the
                logo.
              </p>

              <div className="prog-hero-cta">
                <Link to="/employers" className="prog-btn-primary">
                  Talk to us about your employer brand →
                </Link>
              </div>
            </motion.div>

            {/* =================================================
                HERO MOCKUP
                ================================================= */}

            <motion.div
              className="eb-mockup-stage"
              aria-hidden="true"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
            >

              {/* CAREER PAGE BROWSER */}

              <div className="eb-browser">
                <div className="eb-browser-chrome">
                  <span className="eb-dot" style={{ background: '#E4502F' }} />
                  <span className="eb-dot" style={{ background: '#D6A039' }} />
                  <span className="eb-dot" style={{ background: '#1CAA3C' }} />
                  <div className="eb-url">yourcompany.com/careers</div>
                </div>

                <div className="eb-browser-body">
                  <div className="eb-page-nav">
                    <div className="eb-page-logo" />
                    <div className="eb-page-navlinks">
                      <span>About</span>
                      <span>Team</span>
                      <span>Roles</span>
                    </div>
                  </div>

                  <div className="eb-page-hero">
                    <div className="eb-page-h1-text">
                      Build the next chapter with us.
                    </div>
                    <div className="eb-page-sub-text">
                      Small team, real ownership, and a mission worth showing
                      up for.
                    </div>
                    <div className="eb-page-btn">See open roles</div>
                  </div>

                  <div className="eb-pillars">
                    {pillars.map((pillar, i) => (
                      <div className="eb-pillar" key={i}>
                        <div className="eb-pillar-icon">{pillar.icon}</div>
                        <div className="eb-pillar-text">{pillar.text}</div>
                      </div>
                    ))}
                  </div>

                  <div className="eb-team-strip">
                    {teamColors.map((color, i) => (
                      <span className="eb-avatar" style={{ background: color }} key={i}>
                        <PersonIcon />
                      </span>
                    ))}
                    <span className="eb-team-label">Meet the team →</span>
                  </div>

                  <div className="eb-roles">
                    {roles.map((role) => (
                      <div className="eb-role" key={role.title}>
                        <span>{role.title}</span>
                        <span className="eb-role-tag">{role.tag}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* EVP DOCUMENT */}

              <div className="eb-doc">
                <div className="eb-doc-head">
                  <div className="eb-doc-logo" />
                  <div className="eb-doc-tag">EVP ONE-PAGER</div>
                </div>

                <div className="eb-doc-title-text">Why join us</div>

                {evpRows.map((row) => (
                  <div className="eb-doc-row" key={row.title}>
                    <div className="eb-doc-icon">{row.icon}</div>
                    <div className="eb-doc-text">
                      <strong>{row.title}</strong>
                      <span>{row.text}</span>
                    </div>
                  </div>
                ))}
              </div>

            </motion.div>
          </div>
        </header>

        {/* =====================================================
            WHAT WE BUILD
            ===================================================== */}

        <section className="prog-section">

          <div className="prog-section-head">
            <h2>What we build</h2>
            <p>
              Not a logo refresh — the specific things that change whether a
              candidate takes your call.
            </p>
          </div>

          <div className="prog-grid-3">
            {buildCards.map((card, index) => {
              const Icon = card.icon;

              return (
                <motion.div
                  key={card.title}
                  className="eb-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.45, delay: index * 0.05 }}
                >
                  <div className="eb-card-icon">
                    <Icon />
                  </div>

                  <h3>{card.title}</h3>
                  <p>{card.description}</p>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* =====================================================
            HOW IT WORKS
            ===================================================== */}

        <section className="prog-section tint">

          <div className="prog-section-head">
            <h2>How it works</h2>
          </div>

          <div className="prog-steps">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                className="prog-step"
                initial={{ opacity: 0, x: -15 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.45, delay: index * 0.06 }}
              >
                <div className="prog-step-num">{step.number}</div>
                <div>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* =====================================================
            CTA
            ===================================================== */}

        <section className="prog-cta-strip">
          <h2>Want candidates to recognize you before they even apply?</h2>
          <p>
            Tell us where you're starting from — we'll help you figure out
            what's worth building first.
          </p>
          <div className="prog-hero-cta">
            <Link to="/employers" className="prog-btn-primary">
              Talk to us →
            </Link>
          </div>
        </section>

      </main>
    </>
  );
};

export default EmployerBrandingPage;